import { useAppSelector } from 'app/hooks';
import { useMemo } from 'react';
import { findZoneByAirHandlerId } from 'utils/configurations';
import { calculateLinesetLengthFromPoints } from 'utils/linesets';

type LinesetPairInfo = {
  linesetId: string;
  condenserName: string;
  zoneName: string;
  length: number;
};

type CondenserGroup = {
  condenserUniqueId: string;
  condenserName: string;
  pairs: LinesetPairInfo[];
  totalLength: number;
};

export default function LinesetSummaryPanel() {
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const { structure } = useAppSelector((state) => state.structure);

  const activeConfiguration = configurations[activeConfig];

  const condenserGroups = useMemo<CondenserGroup[]>(() => {
    if (!activeConfiguration?.lineSets?.length) return [];

    const groupsMap = new Map<string, CondenserGroup>();

    activeConfiguration.lineSets.forEach((lineset) => {
      const condenser = activeConfiguration.condensers.find(
        (c) => c.uniqueId === lineset.condenserUniqueId
      );
      if (!condenser) return;

      const zoneResult = findZoneByAirHandlerId(
        activeConfiguration,
        lineset.airHandlerUniqueId
      );
      if (!zoneResult) return;

      const length =
        zoneResult.zone.distanceFromCondenser ??
        calculateLinesetLengthFromPoints(lineset, structure);

      const pair: LinesetPairInfo = {
        linesetId: lineset.uniqueId,
        condenserName: condenser.name,
        zoneName: zoneResult.zone.name,
        length
      };

      const existingGroup = groupsMap.get(condenser.uniqueId);
      if (existingGroup) {
        existingGroup.pairs.push(pair);
        existingGroup.totalLength += length;
      } else {
        groupsMap.set(condenser.uniqueId, {
          condenserUniqueId: condenser.uniqueId,
          condenserName: condenser.name,
          pairs: [pair],
          totalLength: length
        });
      }
    });

    return Array.from(groupsMap.values());
  }, [activeConfiguration, structure]);

  if (condenserGroups.length === 0) {
    return (
      <div className='text-center text-gray-500'>
        No line sets configured yet.
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {condenserGroups.map((group) => (
        <div
          key={group.condenserUniqueId}
          className='rounded-lg border border-gray-200 bg-neutral-100 p-3'
        >
          <div className='mb-2 flex items-center justify-between border-b border-neutral-300 pb-2'>
            <span className='font-semibold text-gray-800'>
              {group.condenserName}
            </span>
            <span className='text-sm font-medium text-gray-600'>
              Total: {group.totalLength} ft
            </span>
          </div>
          <div className='flex flex-col gap-1'>
            {group.pairs.map((pair) => (
              <div
                key={pair.linesetId}
                className='flex items-center justify-between text-sm'
              >
                <span className='text-gray-600'>
                  {pair.condenserName} <span className='text-gray-400'>→</span>{' '}
                  {pair.zoneName}
                </span>
                <span className='font-medium text-gray-700'>
                  {pair.length} ft
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
